import React, { useEffect, useRef } from 'react';
import { getLayers, layerNames, MapyAttributionLogoControl } from '../layers';
import { Wgs84Coordinates } from '../../../models';
import { MarkerClusterGroup } from 'leaflet.markercluster';
import L, { LatLng, latLngBounds } from 'leaflet';
import { LayerType } from '../map-types';
import customRedMarker from './../assets/bigRedMarker.png';
import './map-loader.css';

import ReactDOM from 'react-dom';
import { MarkerPopupConfig } from './marker-popup-factory';
import { CrzpMarker } from '../map-types';

const defaultIcon = L.icon({
  iconUrl: customRedMarker,
  iconSize: [25, 33],
  iconAnchor: [12, 33],
  popupAnchor: [0, -33],
});

L.Marker.prototype.options.icon = defaultIcon;

interface CrzpMapOptions {
  defaultWgs84Position: Wgs84Coordinates | Wgs84Coordinates[];
  defaultZoom: number;
  mode?: 'POINT_ADD' | 'PREVIEW';
  mapElement?: HTMLElement;
}

export class CrzpMap {
  public ref: L.Map;
  private markerCluster: any;

  private defaultWgs84Position: Wgs84Coordinates | Wgs84Coordinates[];
  private defaultZoom: number;

  constructor(options: CrzpMapOptions) {
    if (options.mapElement) {
      this.ref = L.map(options.mapElement);
    } else {
      this.ref = L.map('map');
    }
    this.defaultWgs84Position = options.defaultWgs84Position;
    this.defaultZoom = options.defaultZoom;
  }

  public centerMapWgs84ToDefaults(zoom?: number) {
    if (Array.isArray(this.defaultWgs84Position)) {
      const mappedCoords = this.defaultWgs84Position.map(
        ({ lat, lon }) => new LatLng(Number(lat), Number(lon))
      );

      const markerBounds = latLngBounds(mappedCoords);
      this.ref.fitBounds(markerBounds);
    } else {
      this.ref.setView(
        new LatLng(
          Number(this.defaultWgs84Position.lat),
          Number(this.defaultWgs84Position.lon)
        ),
        zoom ?? this.defaultZoom
      );
    }
  }

  public disableZoom() {
    this.ref.touchZoom.disable();
    this.ref.doubleClickZoom.disable();
    this.ref.scrollWheelZoom.disable();
    this.ref.boxZoom.disable();
    this.ref.keyboard.disable();
    this.setupCtrlZoom();
  }

  public initLayers() {
    const control = L.control.layers(
      {},
      {},
      {
        position: 'topleft',
      }
    );

    const layers = getLayers();

    // Add base layers
    Object.entries(layerNames).forEach(([type, name]) => {
      control.addBaseLayer(layers[type as LayerType], name);
    });

    // Add control to map
    this.ref.addControl(control);

    // Set initial layer
    this.ref.addLayer(layers[LayerType.BASE]);

    // Create marker cluster
    this.markerCluster = new MarkerClusterGroup({
      chunkedLoading: true,
      chunkInterval: 50,
      chunkDelay: 1,
      maxClusterRadius: 80, //default value = 80
    });

    this.ref.addLayer(this.markerCluster);
  }

  public clearMarkers() {
    // TODO: close popup before clearing markers - problem in overview map only
    // this.markerCluster.getLayers?.()?.forEach((layer: any) => {
    //   console.log({ layer });
    //   console.log({ cl: layer.closePopup });
    //   layer?.closePopup?.();
    // });

    this.markerCluster.clearLayers();
  }

  public addLeafletMarkers(
    markers: { leafletMarker: L.Marker; markerData: CrzpMarker }[],
    config?: {
      /**
       * For simple string popups
       */
      popupString?: string;
      markerPopupConfig?: MarkerPopupConfig<any>; // For interactive React popups
    }
  ) {
    // console.log('Adding markers to map', markers);
    markers.forEach(({ leafletMarker, markerData }) => {
      if (config?.popupString) {
        leafletMarker.bindPopup(config.popupString);
      } else if (config?.markerPopupConfig) {
        const { fetchData, PopupBodyComponent } = config.markerPopupConfig;

        const popupContainer = document.createElement('div', {});
        //  popupContainer.className = 'custom-leaflet-popup-content';
        leafletMarker.bindPopup(popupContainer, {
          minWidth: 300,
        });

        leafletMarker.on('popupopen', async () => {
          popupContainer.innerHTML = 'Načítání...';
          try {
            const data = await fetchData(markerData);

            ReactDOM.render(
              React.createElement(PopupBodyComponent, {
                data,
                marker: markerData,
              }),
              popupContainer
            );
          } catch (error) {
            console.error('Failed to load or render popup data:', error);
            popupContainer.innerHTML = `Chyba při načítání obsahu: ${
              markerData?.id ? `( ${markerData.id} )` : ''
            }`;
          }
        });

        leafletMarker.on('popupclose', () => {
          ReactDOM.unmountComponentAtNode(popupContainer);
          popupContainer.innerHTML = '';
        });
      }
      this.markerCluster.addLayer(leafletMarker);
    });
  }

  public destroy() {
    this.clearMarkers();
    this.removeCtrlZoom();
    this.ref.remove();
  }

  private handleKeyChange = (e: KeyboardEvent) => {
    if (e.ctrlKey) {
      this.ref.scrollWheelZoom.enable();
    } else {
      this.ref.scrollWheelZoom.disable();
    }
  };

  private handleBlur = () => {
    this.ref.scrollWheelZoom.disable();
  };

  private setupCtrlZoom() {
    this.ref.scrollWheelZoom.disable();
    document.addEventListener('keydown', this.handleKeyChange);
    document.addEventListener('keyup', this.handleKeyChange);
    window.addEventListener('blur', this.handleBlur);
  }

  private removeCtrlZoom() {
    document.removeEventListener('keydown', this.handleKeyChange);
    document.removeEventListener('keyup', this.handleKeyChange);
    window.removeEventListener('blur', this.handleBlur);
  }

  public getLeftTopCornerCoordinates() {
    const bounds = this.ref.getBounds();

    return bounds.getNorthWest();
  }

  public getRightBottomCornerCoordinates() {
    const bounds = this.ref.getBounds();

    return bounds.getSouthEast();
  }

  public showLoader(message?: string) {
    this.ref.getContainer().classList.add('map-loading');
    this.ref
      .getContainer()
      .setAttribute('data-loading-message', message || 'Načítání...');
  }
  public hideLoader() {
    this.ref.getContainer().classList.remove('map-loading');
  }
}

export function useMap(
  options: CrzpMapOptions,
  containerRef: React.RefObject<HTMLElement>,
  onMapReady?: (map: CrzpMap | null) => void
) {
  const mapRef = useRef<CrzpMap | null>(null);
  // remember last zoom level and center (center wont be needed probably)
  const lastViewRef = useRef<{ zoom: number; center: L.LatLng } | null>(null);

  const logoControl = useRef<L.Control | null>(null);

  const { defaultZoom, defaultWgs84Position, mode } = options;

  useEffect(() => {
    let mapInstance: CrzpMap | null = null;

    if (!mapRef.current) {
      mapInstance = new CrzpMap({
        defaultZoom,
        defaultWgs84Position,
        mode,
        mapElement: containerRef.current ?? undefined,
      });
      mapRef.current = mapInstance;

      if (lastViewRef.current) {
        mapInstance?.centerMapWgs84ToDefaults?.(lastViewRef.current.zoom);
      } else {
        mapInstance?.centerMapWgs84ToDefaults?.();
      }
      mapRef.current.initLayers();
      mapRef.current.disableZoom();

      // add mapy.com logo/link - we are using their map tiles
      if (logoControl.current === null) {
        logoControl.current = new MapyAttributionLogoControl().addTo(
          mapRef.current.ref
        );
      }

      const currentLeafletMap = mapInstance.ref;

      const onViewChange = () => {
        if (currentLeafletMap) {
          lastViewRef.current = {
            zoom: currentLeafletMap?.getZoom?.(),
            center: currentLeafletMap?.getCenter?.(),
          };
        }
      };

      currentLeafletMap?.on('zoomend', onViewChange);
      currentLeafletMap?.on('moveend', onViewChange);

      onMapReady?.(mapRef.current);
    }

    return () => {
      if (mapRef.current) {
        if (mapRef.current?.ref) {
          try {
            lastViewRef.current = {
              zoom: mapRef.current?.ref?.getZoom?.(),
              center: mapRef.current?.ref?.getCenter?.(),
            };
          } catch (e) {
            console.warn('MAP: Could not get map state before destroying:', e);
          }
          mapRef.current?.ref.off?.('zoomend');
          mapRef.current?.ref.off?.('moveend');
        }
        logoControl.current?.remove?.();
        logoControl.current = null;
        mapRef.current.destroy();
        mapRef.current = null;
        onMapReady?.(null);
      }
    };
  }, [onMapReady, defaultWgs84Position, defaultZoom, mode, containerRef]);

  return mapRef;
}
